import React, { useMemo, useState } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { 
  User, Users, MessageSquare, Calendar, TrendingUp, 
  Layers, DollarSign, Target, BarChart3,
  Zap, Star, Shield, ThumbsUp, ThumbsDown, AlertTriangle, Clock, Meh, MessageCircle
} from 'lucide-react';
import { 
  ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, 
  Radar, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell,
  ScatterChart, Scatter, ZAxis
} from 'recharts';
import { PERSONA_COLORS } from '../../utils/personaColors';
import { NPS_CATEGORIES, EXPERIENCE_INDICATORS, MANAGER_ARCHETYPES, generateEvidence } from '../../utils/managerConstants';

const ManagerProfile = ({ managerStats, groupStats, clientData = [] }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [selectedManager, setSelectedManager] = useState(null);
  const [sortBy, setSortBy] = useState('grossProfit');

  // Use group-based stats if available, otherwise fall back to plan stats
  const stats = groupStats?.length > 0 ? groupStats : managerStats;

  // Build NPS lookup by manager from client data
  const npsDataByManager = useMemo(() => {
    const npsMap = {};
    
    clientData.filter(c => !c.isChildPlan).forEach(client => {
      const mgr = client.manager || 'Unknown';
      if (!npsMap[mgr]) {
        npsMap[mgr] = {
          scores: [],
          promoters: 0,
          passives: 0,
          detractors: 0,
          totalResponses: 0
        };
      }
      
      if (client.npsScore !== null && client.npsScore !== undefined) {
        npsMap[mgr].scores.push(client.npsScore);
        npsMap[mgr].totalResponses += 1;
        
        if (client.npsScore >= 9) npsMap[mgr].promoters += 1;
        else if (client.npsScore >= 7) npsMap[mgr].passives += 1;
        else npsMap[mgr].detractors += 1;
      }
    });
    
    // Calculate averages and NPS score
    Object.keys(npsMap).forEach(mgr => {
      const data = npsMap[mgr];
      data.avgNPS = data.scores.length > 0 
        ? data.scores.reduce((a, b) => a + b, 0) / data.scores.length 
        : null;
      // Calculate Net Promoter Score (% promoters - % detractors)
      if (data.totalResponses > 0) {
        data.npsNetScore = Math.round(
          ((data.promoters - data.detractors) / data.totalResponses) * 100
        );
      } else {
        data.npsNetScore = null;
      }
    });
    
    return npsMap;
  }, [clientData]);

  // Calculate portfolio-wide benchmarks
  const benchmarks = useMemo(() => {
    if (!stats || stats.length === 0) return {};
    
    const totalRevenue = stats.reduce((sum, m) => sum + m.totalRevenue, 0);
    const totalGroups = stats.reduce((sum, m) => sum + (m.planCount || m.groupCount || 0), 0);
    const totalMessages = stats.reduce((sum, m) => sum + m.totalMessages, 0);
    const totalMeetings = stats.reduce((sum, m) => sum + m.totalMeetings, 0);
    const totalWorkload = stats.reduce((sum, m) => sum + m.totalWorkloadCost, 0);
    
    const avgGroupCount = totalGroups / stats.length;
    const avgRevenue = totalRevenue / stats.length;
    const avgMessages = totalMessages / stats.length;
    const avgMeetings = totalMeetings / stats.length;
    const avgMargin = stats.reduce((sum, m) => sum + (m.margin || 0), 0) / stats.length;
    const avgWorkload = totalWorkload / stats.length;
    
    // Calculate NPS benchmarks
    const allNPS = Object.values(npsDataByManager)
      .filter(d => d.avgNPS !== null)
      .map(d => d.avgNPS);
    const avgNPS = allNPS.length > 0 ? allNPS.reduce((a, b) => a + b, 0) / allNPS.length : 7;
    
    // Calculate percentiles for classification
    const margins = stats.map(m => m.margin || 0).sort((a, b) => a - b);
    const p25Margin = margins[Math.floor(margins.length * 0.25)] || 0;
    const p75Margin = margins[Math.floor(margins.length * 0.75)] || 0;
    
    return {
      avgGroupCount,
      avgRevenue,
      avgMessages,
      avgMeetings,
      avgMargin,
      avgWorkload,
      avgNPS,
      p25Margin,
      p75Margin,
      totalManagers: stats.length
    };
  }, [stats, npsDataByManager]);

  // Classify managers into archetypes with NPS consideration
  const classifiedManagers = useMemo(() => {
    if (!stats || stats.length === 0) return [];
    
    return stats.map(mgr => {
      const groupCount = mgr.planCount || mgr.groupCount || 0;
      const margin = mgr.margin || 0;
      const messages = mgr.totalMessages || 0;
      const meetings = mgr.totalMeetings || 0;
      const grossProfit = mgr.netVariance || 0;
      const atRiskRatio = (mgr.atRiskClients || 0) / Math.max(groupCount, 1);
      const profitableRatio = (mgr.profitableClients || 0) / Math.max(groupCount, 1);
      
      // Get NPS data for this manager
      const npsData = npsDataByManager[mgr.name] || { avgNPS: null, npsNetScore: null };
      const avgNPS = npsData.avgNPS || mgr.avgNPS || null;
      const hasGoodNPS = avgNPS !== null && avgNPS >= 7.5;
      const hasExcellentNPS = avgNPS !== null && avgNPS >= 8.5;
      const hasLowNPS = avgNPS !== null && avgNPS < 6;
      
      let archetype = 'Balanced Operator';
      let experienceStyle = 'Efficient & Professional';
      
      // Enhanced classification logic with NPS
      if (hasLowNPS || margin < -10 || atRiskRatio > 0.5) {
        archetype = 'Needs Support';
        experienceStyle = 'Needs Attention';
      } else if (hasExcellentNPS && margin > 20 && profitableRatio > 0.5) {
        archetype = 'Client Champion';
        experienceStyle = 'Responsive & Engaged';
      } else if (margin >= benchmarks.p75Margin && grossProfit > 0 && hasGoodNPS) {
        archetype = 'High Performer';
        experienceStyle = 'Efficient & Professional';
      } else if (meetings >= benchmarks.avgMeetings * 1.5 && hasGoodNPS) {
        archetype = 'Service Excellence';
        experienceStyle = 'High-Touch Service';
      } else if (groupCount >= benchmarks.avgGroupCount * 1.3 && margin > benchmarks.avgMargin) {
        archetype = 'Portfolio Leader';
        experienceStyle = 'Efficient & Professional';
      } else if (groupCount < benchmarks.avgGroupCount * 0.7 && margin > 0) {
        archetype = 'Growth Opportunity';
        experienceStyle = 'Responsive & Engaged';
      } else if (messages >= benchmarks.avgMessages * 1.3 && margin < benchmarks.avgMargin * 0.5) {
        archetype = 'Needs Support';
        experienceStyle = 'Under Pressure';
      }
      
      // Calculate normalized scores for radar chart (0-100 scale)
      const maxRevenue = Math.max(...stats.map(m => m.totalRevenue));
      const maxMessages = Math.max(...stats.map(m => m.totalMessages || 0));
      const maxMeetings = Math.max(...stats.map(m => m.totalMeetings || 0));
      const maxGroups = Math.max(...stats.map(m => m.planCount || m.groupCount || 0));
      const maxMargin = Math.max(...stats.map(m => m.margin || 0));
      
      // Calculate client experience score (composite)
      const complianceScore = mgr.avgCompliance || 80;
      const npsScoreNormalized = avgNPS !== null ? (avgNPS / 10) * 100 : 70;
      const clientExperienceScore = (
        (complianceScore * 0.3) + 
        (npsScoreNormalized * 0.5) + 
        (profitableRatio * 100 * 0.2)
      );
      
      // Generate evidence based on metrics
      const evidence = generateEvidence(mgr, avgNPS, benchmarks, atRiskRatio, profitableRatio);
      
      return {
        ...mgr,
        archetype,
        archetypeInfo: MANAGER_ARCHETYPES[archetype],
        experienceStyle,
        experienceInfo: EXPERIENCE_INDICATORS[experienceStyle],
        npsData: {
          ...npsData,
          avgNPS,
          category: avgNPS >= 9 ? 'promoter' : avgNPS >= 7 ? 'passive' : avgNPS !== null ? 'detractor' : null
        },
        clientExperienceScore,
        evidence,
        scores: {
          revenue: maxRevenue > 0 ? (mgr.totalRevenue / maxRevenue) * 100 : 0,
          messages: maxMessages > 0 ? (messages / maxMessages) * 100 : 0,
          meetings: maxMeetings > 0 ? (meetings / maxMeetings) * 100 : 0,
          portfolio: maxGroups > 0 ? (groupCount / maxGroups) * 100 : 0,
          efficiency: maxMargin > 0 ? Math.max(0, (margin / maxMargin) * 100) : 0,
          clientExp: clientExperienceScore
        },
        groupCount,
        grossProfit,
        atRiskRatio,
        profitableRatio
      };
    });
  }, [stats, benchmarks, npsDataByManager]);

  // Aggregate archetype statistics
  const archetypeStats = useMemo(() => {
    const archetypeMap = {};
    
    classifiedManagers.forEach(mgr => {
      const arch = mgr.archetype;
      if (!archetypeMap[arch]) {
        archetypeMap[arch] = {
          name: arch,
          count: 0,
          totalRevenue: 0,
          totalProfit: 0,
          avgNPS: [],
          managers: []
        };
      }
      archetypeMap[arch].count += 1;
      archetypeMap[arch].totalRevenue += mgr.totalRevenue;
      archetypeMap[arch].totalProfit += mgr.grossProfit;
      if (mgr.npsData?.avgNPS !== null) {
        archetypeMap[arch].avgNPS.push(mgr.npsData.avgNPS);
      }
      archetypeMap[arch].managers.push(mgr.name);
    });
    
    // Calculate average NPS per archetype
    Object.values(archetypeMap).forEach(arch => {
      arch.avgNPSScore = arch.avgNPS.length > 0 
        ? arch.avgNPS.reduce((a, b) => a + b, 0) / arch.avgNPS.length 
        : null;
    });
    
    return Object.values(archetypeMap).sort((a, b) => b.count - a.count);
  }, [classifiedManagers]);

  // Sort managers
  const sortedManagers = useMemo(() => {
    return [...classifiedManagers].sort((a, b) => {
      switch (sortBy) {
        case 'grossProfit': return b.grossProfit - a.grossProfit;
        case 'margin': return (b.margin || 0) - (a.margin || 0);
        case 'groups': return b.groupCount - a.groupCount;
        case 'messages': return (b.totalMessages || 0) - (a.totalMessages || 0);
        case 'meetings': return (b.totalMeetings || 0) - (a.totalMeetings || 0);
        case 'nps': return (b.npsData?.avgNPS || 0) - (a.npsData?.avgNPS || 0);
        case 'clientExp': return (b.clientExperienceScore || 0) - (a.clientExperienceScore || 0);
        default: return b.grossProfit - a.grossProfit;
      }
    });
  }, [classifiedManagers, sortBy]);

  // Scatter plot data for manager positioning
  const scatterData = useMemo(() => {
    return classifiedManagers.map(mgr => ({
      name: mgr.name,
      x: mgr.totalWorkloadCost / 1000, // Workload (x-axis)
      y: mgr.totalRevenue / 1000, // Revenue (y-axis)
      z: mgr.groupCount, // Size
      margin: mgr.margin || 0,
      archetype: mgr.archetype,
      color: MANAGER_ARCHETYPES[mgr.archetype]?.color || '#64748b'
    }));
  }, [classifiedManagers]);

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const d = payload[0].payload;
      return (
        <div className={`p-3 rounded-xl border shadow-xl ${isDark ? 'bg-[#1a1a1c] border-white/10' : 'bg-white border-slate-200'}`}>
          <p className={`font-bold text-sm ${isDark ? 'text-white' : 'text-slate-900'}`}>{d.name}</p>
          <p className="text-xs" style={{ color: d.color }}>{d.archetype}</p>
          <div className="mt-2 space-y-1 text-xs">
            <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>
              Revenue: <span className="text-emerald-500 font-medium">${d.y?.toFixed(0)}k</span>
            </p>
            <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>
              Workload: <span className="text-red-500 font-medium">${d.x?.toFixed(0)}k</span>
            </p>
            <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>
              Groups: <span className="font-medium">{d.z}</span>
            </p>
            <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>
              Gross Margin: <span className={`font-medium ${d.margin >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                {d.margin?.toFixed(1)}%
              </span>
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  const RadarTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className={`p-2 rounded-lg border ${isDark ? 'bg-[#1a1a1c] border-white/10' : 'bg-white border-slate-200'}`}>
          <p className={`text-xs ${isDark ? 'text-white' : 'text-slate-900'}`}>
            {payload[0].payload.subject}: <span className="font-bold text-emerald-500">{payload[0].value?.toFixed(0)}</span>
          </p>
        </div>
      );
    }
    return null;
  };

  if (!stats || stats.length === 0) {
    return (
      <div className={`rounded-2xl border p-12 text-center ${isDark ? 'bg-[#111113] border-white/5' : 'bg-white border-slate-200'}`}>
        <Users className="w-12 h-12 mx-auto mb-4 text-slate-400" />
        <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>No manager data available</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Archetype Overview */}
      <div className={`rounded-2xl border ${isDark ? 'bg-[#111113] border-white/5' : 'bg-white border-slate-200 shadow-sm'}`}>
        <div className="p-6 border-b border-white/5">
          <h3 className={`text-lg font-bold flex items-center ${isDark ? 'text-white' : 'text-slate-900'}`}>
            <Target className="w-5 h-5 mr-2 text-purple-500" />
            Manager Archetype Distribution
          </h3>
          <p className={`text-xs mt-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
            Classification based on workload patterns, portfolio size, and performance metrics
          </p>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 p-6">
          {Object.entries(MANAGER_ARCHETYPES).map(([name, info]) => {
            const stat = archetypeStats.find(s => s.name === name);
            const Icon = info.icon;
            return (
              <div 
                key={name}
                className={`p-4 rounded-xl text-center transition-all cursor-pointer hover:scale-105 ${
                  isDark ? 'bg-white/[0.02] hover:bg-white/[0.05]' : 'bg-slate-50 hover:bg-slate-100'
                }`}
                onClick={() => setSelectedManager(stat?.managers?.[0] || null)}
              >
                <Icon className="w-6 h-6 mx-auto mb-2" style={{ color: info.color }} />
                <p className="text-xs font-medium mb-1" style={{ color: info.color }}>{name}</p>
                <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  {stat?.count || 0}
                </p>
                <p className={`text-[10px] ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                  managers
                </p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Manager Scatter Plot */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className={`p-6 rounded-2xl border ${isDark ? 'bg-[#111113] border-white/5' : 'bg-white border-slate-200 shadow-sm'}`}>
          <h3 className={`text-lg font-bold mb-1 flex items-center ${isDark ? 'text-white' : 'text-slate-900'}`}>
            <BarChart3 className="w-5 h-5 mr-2 text-blue-500" />
            Manager Portfolio Map
          </h3>
          <p className={`text-xs mb-4 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
            Revenue vs Workload Cost (bubble size = number of groups)
          </p>
          <div className="h-[350px]">
            <ResponsiveContainer width="100%" height="100%">
              <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.08)'} />
                <XAxis 
                  type="number" 
                  dataKey="x" 
                  name="Workload"
                  tickFormatter={(v) => `$${v}k`}
                  tick={{ fontSize: 10, fill: isDark ? '#94a3b8' : '#64748b' }}
                  label={{ value: 'Workload Cost ($k)', position: 'bottom', offset: 0, fontSize: 10, fill: isDark ? '#64748b' : '#94a3b8' }}
                />
                <YAxis 
                  type="number" 
                  dataKey="y" 
                  name="Revenue"
                  tickFormatter={(v) => `$${v}k`}
                  tick={{ fontSize: 10, fill: isDark ? '#94a3b8' : '#64748b' }}
                  label={{ value: 'Revenue ($k)', angle: -90, position: 'insideLeft', fontSize: 10, fill: isDark ? '#64748b' : '#94a3b8' }}
                />
                <ZAxis type="number" dataKey="z" range={[100, 1000]} />
                <Tooltip content={<CustomTooltip />} />
                <Scatter data={scatterData}>
                  {scatterData.map((entry) => (
                    <Cell key={entry.name} fill={entry.color} fillOpacity={0.7} />
                  ))}
                </Scatter>
              </ScatterChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Archetype Breakdown */}
        <div className={`p-6 rounded-2xl border ${isDark ? 'bg-[#111113] border-white/5' : 'bg-white border-slate-200 shadow-sm'}`}>
          <h3 className={`text-lg font-bold mb-1 flex items-center ${isDark ? 'text-white' : 'text-slate-900'}`}>
            <Layers className="w-5 h-5 mr-2 text-purple-500" />
            Archetype Performance Summary
          </h3>
          <p className={`text-xs mb-4 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
            Revenue and gross profit by manager type
          </p>
          <div className="h-[350px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart 
                data={archetypeStats} 
                layout="vertical"
                margin={{ top: 5, right: 30, left: 10, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.08)'} />
                <XAxis type="number" tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} tick={{ fontSize: 10, fill: isDark ? '#94a3b8' : '#64748b' }} />
                <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 10, fill: isDark ? '#94a3b8' : '#64748b' }} />
                <Tooltip 
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      const d = payload[0].payload;
                      return (
                        <div className={`p-3 rounded-xl border ${isDark ? 'bg-[#1a1a1c] border-white/10' : 'bg-white border-slate-200'}`}>
                          <p className={`font-bold text-sm ${isDark ? 'text-white' : 'text-slate-900'}`}>{d.name}</p>
                          <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{d.count} managers</p>
                          <p className="text-xs text-emerald-500">Revenue: ${(d.totalRevenue/1000).toFixed(0)}k</p>
                          <p className={`text-xs ${d.totalProfit >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                            Gross Profit: {d.totalProfit >= 0 ? '+' : ''}${(d.totalProfit/1000).toFixed(0)}k
                          </p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Bar dataKey="totalRevenue" fill="#10b981" radius={[0, 4, 4, 0]} barSize={14} name="Revenue" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Manager List */}
      <div className={`rounded-2xl border overflow-hidden ${isDark ? 'bg-[#111113] border-white/5' : 'bg-white border-slate-200 shadow-sm'}`}>
        <div className={`p-5 border-b flex items-center justify-between ${isDark ? 'border-white/5' : 'border-slate-200'}`}>
          <div>
            <h3 className={`text-lg font-bold flex items-center ${isDark ? 'text-white' : 'text-slate-900'}`}>
              <User className="w-5 h-5 mr-2 text-blue-500" />
              Manager Profiles
            </h3>
            <p className={`text-xs mt-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
              Click a manager to see detailed profile with client experience insights
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Sort by:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className={`px-3 py-1.5 rounded-lg text-xs border ${
                isDark 
                  ? 'bg-white/5 border-white/10 text-white' 
                  : 'bg-slate-50 border-slate-200 text-slate-700'
              }`}
            >
              <option value="grossProfit">Gross Profit</option>
              <option value="margin">Gross Margin</option>
              <option value="nps">NPS Score</option>
              <option value="clientExp">Client Experience</option>
              <option value="groups">Portfolio Size</option>
              <option value="messages">Emails</option>
              <option value="meetings">Meetings</option>
            </select>
          </div>
        </div>
        
        <div className="divide-y divide-white/5">
          {sortedManagers.map((mgr, idx) => {
            const Icon = mgr.archetypeInfo?.icon || User;
            const isExpanded = selectedManager === mgr.name;
            const npsCategory = mgr.npsData?.category;
            const NPSIcon = npsCategory ? NPS_CATEGORIES[npsCategory]?.icon : Star;
            
            return (
              <div key={mgr.name}>
                <button
                  onClick={() => setSelectedManager(isExpanded ? null : mgr.name)}
                  className={`w-full px-5 py-4 flex items-center gap-4 transition-colors ${
                    isDark 
                      ? 'hover:bg-white/[0.02]' 
                      : 'hover:bg-slate-50'
                  } ${isExpanded ? (isDark ? 'bg-white/[0.02]' : 'bg-slate-50') : ''}`}
                >
                  {/* Rank */}
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                    idx < 3 
                      ? 'bg-emerald-500/20 text-emerald-500' 
                      : isDark ? 'bg-white/5 text-slate-400' : 'bg-slate-100 text-slate-500'
                  }`}>
                    {idx + 1}
                  </div>
                  
                  {/* Manager Info */}
                  <div className="flex-1 text-left">
                    <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{mgr.name}</p>
                    <div className="flex items-center gap-3 mt-0.5">
                      <div className="flex items-center gap-1">
                        <Icon className="w-3 h-3" style={{ color: mgr.archetypeInfo?.color }} />
                        <span className="text-xs" style={{ color: mgr.archetypeInfo?.color }}>{mgr.archetype}</span>
                      </div>
                      {mgr.npsData?.avgNPS !== null && (
                        <div className="flex items-center gap-1">
                          <NPSIcon className="w-3 h-3" style={{ color: NPS_CATEGORIES[npsCategory]?.color || '#64748b' }} />
                          <span className="text-xs" style={{ color: NPS_CATEGORIES[npsCategory]?.color || '#64748b' }}>
                            NPS {mgr.npsData.avgNPS.toFixed(1)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Metrics */}
                  <div className="hidden md:grid grid-cols-6 gap-4 text-right">
                    <div>
                      <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Groups</p>
                      <p className={`font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{mgr.groupCount}</p>
                    </div>
                    <div>
                      <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Revenue</p>
                      <p className="font-bold text-emerald-500">${(mgr.totalRevenue/1000).toFixed(0)}k</p>
                    </div>
                    <div>
                      <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Emails</p>
                      <p className={`font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{(mgr.totalMessages || 0).toLocaleString()}</p>
                    </div>
                    <div>
                      <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Meetings</p>
                      <p className={`font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{mgr.totalMeetings || 0}</p>
                    </div>
                    <div>
                      <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Gross Margin</p>
                      <p className={`font-bold ${(mgr.margin || 0) >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                        {(mgr.margin || 0).toFixed(1)}%
                      </p>
                    </div>
                    <div>
                      <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Client Exp</p>
                      <p className={`font-bold ${(mgr.clientExperienceScore || 0) >= 70 ? 'text-emerald-500' : (mgr.clientExperienceScore || 0) >= 50 ? 'text-amber-500' : 'text-red-500'}`}>
                        {(mgr.clientExperienceScore || 0).toFixed(0)}
                      </p>
                    </div>
                  </div>
                </button>
                
                {/* Expanded Details */}
                {isExpanded && (
                  <div className={`px-5 pb-5 ${isDark ? 'bg-white/[0.01]' : 'bg-slate-50/50'}`}>
                    <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 pt-4">
                      {/* Radar Chart */}
                      <div className={`p-4 rounded-xl ${isDark ? 'bg-white/[0.02]' : 'bg-white'}`}>
                        <h4 className={`text-xs font-bold uppercase tracking-wider mb-3 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                          Performance Profile
                        </h4>
                        <div className="h-[180px]">
                          <ResponsiveContainer width="100%" height="100%">
                            <RadarChart data={[
                              { subject: 'Revenue', value: mgr.scores?.revenue || 0 },
                              { subject: 'Portfolio', value: mgr.scores?.portfolio || 0 },
                              { subject: 'Emails', value: mgr.scores?.messages || 0 },
                              { subject: 'Meetings', value: mgr.scores?.meetings || 0 },
                              { subject: 'Efficiency', value: mgr.scores?.efficiency || 0 }
                            ]}>
                              <PolarGrid stroke={isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'} />
                              <PolarAngleAxis dataKey="subject" tick={{ fontSize: 10, fill: isDark ? '#94a3b8' : '#64748b' }} />
                              <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fontSize: 8 }} />
                              <Radar 
                                dataKey="value" 
                                stroke={mgr.archetypeInfo?.color || '#10b981'} 
                                fill={mgr.archetypeInfo?.color || '#10b981'} 
                                fillOpacity={0.3} 
                              />
                              <Tooltip content={<RadarTooltip />} />
                            </RadarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                      
                      {/* Detailed Stats */}
                      <div className={`p-4 rounded-xl ${isDark ? 'bg-white/[0.02]' : 'bg-white'}`}>
                        <h4 className={`text-xs font-bold uppercase tracking-wider mb-3 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                          Workload Breakdown
                        </h4>
                        <div className="space-y-3 text-sm">
                          <div className="flex justify-between items-center">
                            <span className={isDark ? 'text-slate-400' : 'text-slate-500'}>
                              <MessageSquare className="w-3 h-3 inline mr-2" />
                              Message Cost
                            </span>
                            <span className="font-medium text-red-500">
                              ${((mgr.totalWorkloadCost - (mgr.totalMeetingCost || 0)) || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className={isDark ? 'text-slate-400' : 'text-slate-500'}>
                              <Calendar className="w-3 h-3 inline mr-2" />
                              Meeting Cost
                            </span>
                            <span className="font-medium text-red-500">
                              ${(mgr.totalMeetingCost || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                            </span>
                          </div>
                          <div className={`flex justify-between items-center pt-2 border-t ${isDark ? 'border-white/5' : 'border-slate-200'}`}>
                            <span className={isDark ? 'text-slate-400' : 'text-slate-500'}>
                              <DollarSign className="w-3 h-3 inline mr-2" />
                              Total Workload
                            </span>
                            <span className="font-bold text-red-500">
                              ${(mgr.totalWorkloadCost || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className={isDark ? 'text-slate-400' : 'text-slate-500'}>
                              <TrendingUp className="w-3 h-3 inline mr-2" />
                              Gross Margin
                            </span>
                            <span className={`font-bold ${mgr.grossProfit >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                              {mgr.grossProfit >= 0 ? '+' : ''}${mgr.grossProfit.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Recommendation */}
                      <div className={`p-4 rounded-xl ${isDark ? 'bg-white/[0.02]' : 'bg-white'}`}>
                        <h4 className={`text-xs font-bold uppercase tracking-wider mb-3 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                          Archetype Analysis
                        </h4>
                        <div className="space-y-3">
                          <div className="flex items-start gap-2">
                            <Icon className="w-5 h-5 mt-0.5" style={{ color: mgr.archetypeInfo?.color }} />
                            <div>
                              <p className="font-medium" style={{ color: mgr.archetypeInfo?.color }}>{mgr.archetype}</p>
                              <p className={`text-xs mt-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                                {mgr.archetypeInfo?.description}
                              </p>
                            </div>
                          </div>
                          <div className={`p-3 rounded-lg ${isDark ? 'bg-white/[0.02]' : 'bg-slate-50'}`}>
                            <p className={`text-xs font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                              <Zap className="w-3 h-3 inline mr-1 text-amber-500" />
                              Recommendation
                            </p>
                            <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                              {mgr.archetypeInfo?.recommendation}
                            </p>
                          </div>
                          <p className={`text-[10px] ${isDark ? 'text-slate-600' : 'text-slate-400'}`}>
                            <Shield className="w-3 h-3 inline mr-1" />
                            Criteria: {mgr.archetypeInfo?.criteria}
                          </p>
                        </div>
                      </div>
                      
                      {/* Client Experience & Evidence */}
                      <div className={`p-4 rounded-xl ${isDark ? 'bg-white/[0.02]' : 'bg-white'}`}>
                        <h4 className={`text-xs font-bold uppercase tracking-wider mb-3 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                          Client Experience & Evidence
                        </h4>
                        
                        {/* NPS Summary */}
                        {mgr.npsData?.avgNPS !== null && (
                          <div className={`mb-3 p-3 rounded-lg ${isDark ? 'bg-white/[0.02]' : 'bg-slate-50'}`}>
                            <div className="flex items-center justify-between mb-2">
                              <span className={`text-xs font-medium ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                                <Star className="w-3 h-3 inline mr-1 text-amber-500" />
                                NPS Score
                              </span>
                              <span className="text-lg font-bold" style={{ color: NPS_CATEGORIES[mgr.npsData.category]?.color }}>
                                {mgr.npsData.avgNPS.toFixed(1)}
                              </span>
                            </div>
                            <div className="flex gap-2 text-[10px]">
                              {mgr.npsData.promoters > 0 && (
                                <span className="text-emerald-500">
                                  <ThumbsUp className="w-3 h-3 inline" /> {mgr.npsData.promoters} promoters
                                </span>
                              )}
                              {mgr.npsData.passives > 0 && (
                                <span className="text-amber-500">
                                  <Meh className="w-3 h-3 inline" /> {mgr.npsData.passives} passive
                                </span>
                              )}
                              {mgr.npsData.detractors > 0 && (
                                <span className="text-red-500">
                                  <ThumbsDown className="w-3 h-3 inline" /> {mgr.npsData.detractors} detractors
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {/* Service Style */}
                        <div className="mb-3">
                          <div className="flex items-center gap-2 mb-1">
                            {mgr.experienceInfo?.icon && <mgr.experienceInfo.icon className="w-4 h-4" style={{ color: mgr.experienceInfo?.color }} />}
                            <span className="text-xs font-medium" style={{ color: mgr.experienceInfo?.color }}>
                              {mgr.experienceStyle}
                            </span>
                          </div>
                          <p className={`text-[10px] ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                            {mgr.experienceInfo?.description}
                          </p>
                        </div>
                        
                        {/* Evidence */}
                        <div className="space-y-1.5">
                          <p className={`text-[10px] font-medium uppercase tracking-wider ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                            Evidence & Insights
                          </p>
                          {mgr.evidence?.evidence?.map((e, i) => (
                            <div key={i} className={`flex items-start gap-2 text-[11px] ${
                              e.type === 'positive' ? 'text-emerald-500' : 
                              e.type === 'negative' ? 'text-red-500' : 
                              e.type === 'warning' ? 'text-amber-500' : 
                              isDark ? 'text-slate-400' : 'text-slate-500'
                            }`}>
                              {e.type === 'positive' && <ThumbsUp className="w-3 h-3 mt-0.5 flex-shrink-0" />}
                              {e.type === 'negative' && <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />}
                              {e.type === 'warning' && <Clock className="w-3 h-3 mt-0.5 flex-shrink-0" />}
                              {e.type === 'neutral' && <MessageCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />}
                              <span>{e.text}</span>
                            </div>
                          ))}
                          {mgr.evidence?.insights?.map((insight, i) => (
                            <div key={`insight-${i}`} className={`flex items-start gap-2 text-[11px] ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                              <Zap className="w-3 h-3 mt-0.5 flex-shrink-0 text-purple-500" />
                              <span className="italic">{insight}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ManagerProfile;
